import { UNKNOWN_FIELD_POLICIES } from '@platform/form-rules';
import { z } from 'zod';

/**
 * An allowed origin: an exact origin (`https://example.com`) or a wildcard-subdomain origin
 * (`https://*.example.com`). No path, no trailing slash. FR-5.3.
 */
export const originPatternSchema = z
  .string()
  .regex(/^https?:\/\/(\*\.)?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*(:\d{1,5})?$/iu, 'must be an origin like https://example.com or https://*.example.com');

export const LOCALE_CODES = ['en', 'ru'] as const;
/**
 * CAPTCHA providers (FR-9.5): Cloudflare Turnstile, and Google reCAPTCHA in its two kinds — v2 shows
 * a challenge, v3 shows nothing and scores the visitor. A provider's keys work for that kind only.
 */
export const CAPTCHA_PROVIDERS = ['none', 'turnstile', 'recaptcha_v2', 'recaptcha_v3'] as const;
export type CaptchaProvider = (typeof CAPTCHA_PROVIDERS)[number];
/** The score reCAPTCHA v3 must give a visitor, unless the form says otherwise. Google's own suggestion. */
export const DEFAULT_CAPTCHA_MIN_SCORE = 0.5;

/**
 * A provider's public site key. Both vendors issue keys of letters, digits, `_` and `-`; nothing else
 * is accepted, because the key is written into the snippets and pages as code (§19 #52).
 */
export const captchaSiteKeySchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_-]{1,200}$/u, 'must be a site key as the provider shows it: letters, digits, "_" and "-"');
const captchaKeys = {
  /** Public: it ships in the page. */
  siteKey: captchaSiteKeySchema,
  /** The provider secret, encrypted to the ingest key pair (spec §18 #19). Never plaintext. */
  secretEncrypted: z.string().min(1),
};
export const captchaMinScoreSchema = z.number().min(0).max(1);

export const captchaSettingsSchema = z.discriminatedUnion('provider', [
  z.object({ provider: z.literal('none') }),
  z.object({ provider: z.literal('turnstile'), ...captchaKeys }),
  z.object({ provider: z.literal('recaptcha_v2'), ...captchaKeys }),
  /** `minScore`: 0.0 is surely a bot, 1.0 surely a person; a visitor scored below it is refused. */
  z.object({ provider: z.literal('recaptcha_v3'), ...captchaKeys, minScore: captchaMinScoreSchema.default(DEFAULT_CAPTCHA_MIN_SCORE) }),
]);
export type CaptchaSettings = z.infer<typeof captchaSettingsSchema>;

/**
 * Every setting a project can define and a form can override (FR-5.1, FR-5.2). Overrides are
 * shallow: a form replaces a whole top-level key, never part of one. Adding a key here is enough
 * for it to be inherited, overridable and covered by the resolution tests.
 */
export const settingsSchema = z.object({
  allowedOrigins: z.array(originPatternSchema).max(50),
  allowLocalhost: z.boolean(),
  locale: z.enum(LOCALE_CODES),
  unknownFieldPolicy: z.enum(UNKNOWN_FIELD_POLICIES),
  honeypot: z.boolean(),
  /**
   * Whether a submission must carry a timing token (FR-10.2). On by default. A form that has to work
   * with JavaScript disabled turns it off — a page without JavaScript cannot fetch a token — and
   * relies on the honeypot and rate limits instead.
   */
  timingToken: z.boolean(),
  minFillSeconds: z.number().int().min(0).max(3600),
  captcha: captchaSettingsSchema,
  /** Where a no-JS form post is redirected (FR-6.5). Must be on an allowed origin. */
  successUrl: z.url().nullable(),
  errorUrl: z.url().nullable(),
  retentionDays: z.number().int().min(1).max(365),
  storeIp: z.boolean(),
  maxFields: z.number().int().min(1).max(200),
  maxValueLength: z.number().int().min(1).max(100_000),
  maxPayloadBytes: z.number().int().min(1024).max(1_048_576),
});
export type Settings = z.infer<typeof settingsSchema>;
export type SettingKey = keyof Settings;

/** What a project or a form stores: only the keys it sets itself. */
export const settingsOverrideSchema = settingsSchema.partial();
export type SettingsOverride = z.infer<typeof settingsOverrideSchema>;

/** Platform defaults, used for any key neither the project nor the form sets. */
export const DEFAULT_SETTINGS: Settings = {
  allowedOrigins: [],
  allowLocalhost: false,
  locale: 'en',
  unknownFieldPolicy: 'store_as_extra',
  honeypot: true,
  timingToken: true,
  minFillSeconds: 3,
  captcha: { provider: 'none' },
  successUrl: null,
  errorUrl: null,
  retentionDays: 90,
  storeIp: true,
  maxFields: 50,
  maxValueLength: 10_000,
  maxPayloadBytes: 102_400,
};

// cast: Object.keys() returns string[]; the keys of a Zod object's shape are exactly its field names.
export const SETTING_KEYS = Object.keys(settingsSchema.shape) as SettingKey[];
